// BID de Frete — lado do embarcador (épico PO1-2413; H2 PO1-2841, H3 PO1-2844,
// H4 PO1-2843, H6 PO1-2845, H7 PO1-2847, H8 PO1-2848, H9 PO1-2849). Mesmo
// molde do leilaoFreteApi: toda chamada leva `embarcadorId` do seletor do
// cabeçalho (useCompanies) e o backend_hub (módulo gobid-bid) confere o escopo.
//
// Contrato: README do módulo gobid-bid no backend_hub (rotas conferidas
// contra controllers/BidFreteController.js). Erros: { error, code, ...details }
// (ex.: 400 `dados_invalidos` com `errors:{campo:msg}`; 409 `baseline_existente`
// com `precisaConfirmar` -> reenviar com { confirmar: true }).
// - lista: { items, totalCount, page, pageSize, cards:{ rascunho, publicado, encerrado, cancelado }, serverNow }
// - bid: colunas do BidFrete em camelCase (ufs/regioes "SP;MG") + embarcadorFantasia,
//        filtros, etapas:[{ ordem, etapa, status, prazo }], etapaAtual (string), execucoes, participantes:{ total, ESTADO:n }
// - 202 de baseline/pacote/executar: { execucao } (executar também { candidatas })
// Rotas da 2ª metade (convite, adjudicação) seguem a spec; ver os comentários de cada função.
import api from "./api";

const BASE = "/gobid/bids";

// ── Lista, criação e cabeçalho ───────────────────────────────────────────────

export async function listarBids(embarcadorId, { situacao, preset, busca, page, pageSize } = {}) {
  const { data } = await api.get(BASE, {
    params: { embarcadorId, situacao: situacao || undefined, preset: preset || undefined, busca: busca || undefined, page, pageSize },
  });
  return data;
}

// Escopo + critérios + proposta (passos do wizard). Devolve o BID criado.
export async function criarBid(embarcadorId, dados) {
  const { data } = await api.post(BASE, { ...dados, embarcadorId });
  return data;
}

// Prévia do escopo (só conta): { qtdNf, qtdComCte, qtdRomaneiosLotacao,
// candidatas: { tabela, conector, total }, estimativaCalculos, estimativaMinutos }.
export async function previaBid(embarcadorId, escopo) {
  const { data } = await api.post(`${BASE}/previa`, { ...escopo, embarcadorId });
  return data;
}

export async function obterBid(embarcadorId, id) {
  const { data } = await api.get(`${BASE}/${id}`, { params: { embarcadorId } });
  return data;
}

export async function editarBid(embarcadorId, id, dados) {
  const { data } = await api.put(`${BASE}/${id}`, { ...dados, embarcadorId });
  return data;
}

export async function excluirBid(embarcadorId, id) {
  const { data } = await api.delete(`${BASE}/${id}`, { params: { embarcadorId } });
  return data;
}

// ── Etapas assíncronas (202) e progresso ─────────────────────────────────────

// Recriar exige `confirmar: true` (marca PACOTE e SIMULACAO como desatualizados).
export async function criarBaseline(embarcadorId, id, { confirmar } = {}) {
  const { data } = await api.post(`${BASE}/${id}/baseline`, { embarcadorId, confirmar: Boolean(confirmar) });
  return data;
}

export async function gerarPacote(embarcadorId, id) {
  const { data } = await api.post(`${BASE}/${id}/pacote`, { embarcadorId });
  return data;
}

export async function executarBid(embarcadorId, id) {
  const { data } = await api.post(`${BASE}/${id}/executar`, { embarcadorId });
  return data;
}

export async function listarExecucoes(embarcadorId, id) {
  const { data } = await api.get(`${BASE}/${id}/execucoes`, { params: { embarcadorId } });
  return data;
}

// ── Baseline ─────────────────────────────────────────────────────────────────

// { qtdNf, qtdNfComparavel, qtdNfSemCte, qtdEmbarques, vrBaselineTotal,
//   vrComplementarTotal, porUf: [{ uf, qtdNf, qtdComparavel, vrFrete, vrComplementar }],
//   porTransportadora: [{ idTransportadora, nome, qtdNf, qtdComparavel, vrFrete, vrComplementar }] }
export async function resumoBaseline(embarcadorId, id) {
  const { data } = await api.get(`${BASE}/${id}/baseline/resumo`, { params: { embarcadorId } });
  return data;
}

export async function notasBaseline(embarcadorId, id, { page, pageSize, uf, idTransportadora, comparavel, busca } = {}) {
  const { data } = await api.get(`${BASE}/${id}/baseline/notas`, {
    params: { embarcadorId, page, pageSize, uf: uf || undefined, idTransportadora: idTransportadora || undefined, comparavel, busca: busca || undefined },
  });
  return data;
}

// ── Resultado (ranking, notas, detalhe fiscal, export) ───────────────────────

// { nivel, area, areas: [{ nivel, area, rotulo }], candidatas: [{ posicao, idTransportadora, nome, fonte,
//   idFreteTabela, score, vrFrete, vrImposto, vrBaseline, vrComplementar, vrEconomia, prazoMedioDias,
//   prazoMaximoDias, cobertura, qtdNf, qtdCalculada, ehIncumbente, coberturaParcial }] }
export async function rankingBid(embarcadorId, id, { nivel, area } = {}) {
  const { data } = await api.get(`${BASE}/${id}/resultado/ranking`, { params: { embarcadorId, nivel: nivel || undefined, area: area || undefined } });
  return data;
}

// Matriz paginada no servidor: { items:[{ idNotaFiscal, numero, serie, dataEmissao, ufDestino, cidadeDestino,
//   transportadoraAtual, pesoBruto, valorMercadoria, vrFrete (cobrado), vrComplementar (custo extra), comparavel,
//   melhorValor, economia, economiaPct, melhores:[{ idTransportadora, nome, idFreteTabela, fonte, vrFrete, diasPrazo }],
//   qtdCalculada, qtdNaoCalculada, qtdErro }], totalCount, page, pageSize }
// status: COMPARAVEL | SEM_CTE | SEM_CALCULO | ERRO; faixaEconomia: negativa | 0-5 | 5-15 | 15+
export async function notasResultado(embarcadorId, id, { page, pageSize, uf, idTransportadora, idNegocio, idModal, status, faixaEconomia, busca, apenasComparaveis } = {}) {
  const { data } = await api.get(`${BASE}/${id}/resultado/notas`, {
    params: {
      embarcadorId,
      page,
      pageSize,
      uf: uf || undefined,
      idTransportadora: idTransportadora || undefined,
      idNegocio: idNegocio || undefined,
      idModal: idModal || undefined,
      status: status || undefined,
      faixaEconomia: faixaEconomia || undefined,
      busca: busca || undefined,
      apenasComparaveis: apenasComparaveis ? 1 : undefined,
    },
  });
  return data;
}

// Detalhe fiscal de uma NF: { nota (com `candidatas` gravadas), detalhe (resposta da
// instância do BID, recalculado sob demanda, sem gravar), motivo? }.
export async function detalheNotaResultado(embarcadorId, id, idNotaFiscal) {
  const { data } = await api.get(`${BASE}/${id}/resultado/notas/${idNotaFiscal}/detalhe`, { params: { embarcadorId } });
  return data;
}

// Export xlsx (abas Resumo e NFs, até 300 mil NFs). Devolve o Blob para o downloadBlob.
export async function exportarResultado(embarcadorId, id) {
  const { data } = await api.get(`${BASE}/${id}/resultado/export`, { params: { embarcadorId }, responseType: "blob" });
  return data;
}

// { cards:{ qtdNf, qtdNfComparavel, qtdNfSemCte, qtdEmbarques, cobrado, custoExtraCobrado,
//   melhorCenario, economia, economiaPct }, rankingPorNivel, criterios, etapas, etapaAtual, ultimaSimulacao }
export async function resumoResultado(embarcadorId, id) {
  const { data } = await api.get(`${BASE}/${id}/resultado/resumo`, { params: { embarcadorId } });
  return data;
}

// Refaz só o ranking (não recalcula frete): { pesoCusto, pesoPrazo, nivelInicial? }.
export async function atualizarCriterios(embarcadorId, id, { pesoCusto, pesoPrazo, nivelInicial } = {}) {
  const { data } = await api.put(`${BASE}/${id}/criterios`, { embarcadorId, pesoCusto, pesoPrazo, nivelInicial });
  return data;
}

// ── Pacote ───────────────────────────────────────────────────────────────────

// arquivo: "embarques" | "modelo". Devolve { arquivo, url, expiraEm, pacoteEm } (URL de 15 min; abrir em nova aba).
export async function urlPacote(embarcadorId, id, arquivo) {
  const { data } = await api.get(`${BASE}/${id}/pacote/${arquivo}`, { params: { embarcadorId } });
  return data;
}

// ── Convite e propostas (H7; consumidos pela aba Convites) ───────────────────
// Contrato ASSUMIDO (2ª metade do backend em construção):
// - candidatos-convite: { candidatos:[{ idTransportadora, nome, cnpj, naCarteira, atendeDestino,
//   origem, emails:[], temLogin }], maximoConvidados, sourcingDisponivel } (molde do leilão)
// - participantes (GET): { items:[{ id, idTransportadora, cnpj, razaoSocial, nome, email, origem, estado,
//   idConviteTransportadora, cadastroEm, conviteEm, lembreteEm, emailErro, propostaEm, idFreteTabelaProposta }] }
// - participantes (POST): { participantes:[{ idTransportadora? | cnpj, email?, razaoSocial?, origem }] }
//   antes de publicar só registra; depois de publicado já envia o convite.
// - publicar: { prazoPropostas? } -> BID; 409 `etapa_fora_de_ordem` sem pacote.

export async function listarParticipantes(embarcadorId, id) {
  const { data } = await api.get(`${BASE}/${id}/participantes`, { params: { embarcadorId } });
  return data;
}

export async function candidatosConvite(embarcadorId, id) {
  const { data } = await api.get(`${BASE}/${id}/candidatos-convite`, { params: { embarcadorId } });
  return data;
}

// { prazoPropostas? }
export async function publicarBid(embarcadorId, id, dados) {
  const { data } = await api.post(`${BASE}/${id}/publicar`, { ...dados, embarcadorId });
  return data;
}

export async function adicionarParticipante(embarcadorId, id, participantes) {
  const lista = Array.isArray(participantes) ? participantes : [participantes];
  const { data } = await api.post(`${BASE}/${id}/participantes`, { participantes: lista, embarcadorId });
  return data;
}

export async function reenviarConviteBid(embarcadorId, id, participanteId) {
  const { data } = await api.post(`${BASE}/${id}/participantes/${participanteId}/reenviar`, { embarcadorId });
  return data;
}

export async function encerrarPropostas(embarcadorId, id) {
  const { data } = await api.post(`${BASE}/${id}/encerrar-propostas`, { embarcadorId });
  return data;
}

// ── Adjudicação e integração (H9; consumidos pela aba Adjudicação) ───────────
// Contrato ASSUMIDO: adjudicações { items:[{ id, nivelOrigem, areaOrigem, ufs, idTransportadora, transportadora,
//   idFreteTabela, fonte, papel, percentual, vigenciaIni, vigenciaFim, justificativa, linhasSemDestino,
//   idFreteTabelaAtivada, integradaEm }] }; cobertura { ufsSemVencedora, ufsSemPrincipal, somaPercentuais, completa };
// impacto { tabelas:[{ idFreteTabela, nome, idTransportadora, transportadora, ufs, dataValFim }] }.

export async function listarAdjudicacoes(embarcadorId, id) {
  const { data } = await api.get(`${BASE}/${id}/adjudicacoes`, { params: { embarcadorId } });
  return data;
}

// { nivel, area | ufs[], idTransportadora, idFreteTabela, papel, percentual?, vigenciaIni, vigenciaFim, justificativa? }
export async function criarAdjudicacao(embarcadorId, id, dados) {
  const { data } = await api.post(`${BASE}/${id}/adjudicacoes`, { ...dados, embarcadorId });
  return data;
}

export async function excluirAdjudicacao(embarcadorId, id, adjudicacaoId) {
  const { data } = await api.delete(`${BASE}/${id}/adjudicacoes/${adjudicacaoId}`, { params: { embarcadorId } });
  return data;
}

// { ufsSemVencedora: [], ufsSemPrincipal: [], somaPercentuais: { UF: n } }
export async function coberturaAdjudicacao(embarcadorId, id) {
  const { data } = await api.get(`${BASE}/${id}/cobertura`, { params: { embarcadorId } });
  return data;
}

// Tabelas ativas da incumbente que cobrem as UFs (só leitura).
export async function impactoAdjudicacao(embarcadorId, id, ufs) {
  const lista = Array.isArray(ufs) ? ufs.join(";") : ufs;
  const { data } = await api.get(`${BASE}/${id}/impacto`, { params: { embarcadorId, ufs: lista } });
  return data;
}

export async function encerrarAdjudicacao(embarcadorId, id) {
  const { data } = await api.post(`${BASE}/${id}/encerrar-adjudicacao`, { embarcadorId });
  return data;
}

// ── Listas de apoio do wizard (endpoints que FaturamentoFiltros e NovaTabelaFrete já usam) ──

// As regiões do escopo são as geográficas (REGIOES/UFS_POR_REGIAO em regras.js),
// não as RegiaoFrete do embarcador, por isso não entram aqui.
export async function listasEscopo(embarcadorId) {
  const [modais, servicos, estados, negocios, transportadoras, unidades] = await Promise.all([
    api.get("/frete/modais").then((r) => r.data || []).catch(() => []),
    api.get("/frete/tipos-servico").then((r) => r.data || []).catch(() => []),
    api.get("/frete/estados").then((r) => r.data || []).catch(() => []),
    embarcadorId ? api.get(`/embarcadores/${embarcadorId}/negocios`).then((r) => r.data || []).catch(() => []) : Promise.resolve([]),
    embarcadorId ? api.get(`/transportadoras/${embarcadorId}`).then((r) => r.data || []).catch(() => []) : Promise.resolve([]),
    api.get("/frete/unidades").then((r) => r.data || []).catch(() => []),
  ]);
  return { modais, servicos, estados, negocios, transportadoras, unidades };
}

export async function cidadesDoEstado(estadoId) {
  const { data } = await api.get(`/frete/estados/${estadoId}/cidades`);
  return Array.isArray(data) ? data : [];
}
